/**
 * Continuous Action Flow — Post-Action Next Step Prompt
 * ──────────────────────────────────────────────────────
 * Lightweight, NON-DESTRUCTIVE layer that surfaces the next plausible
 * action after a user successfully completes one — via a Sonner action
 * toast. The user must explicitly tap "Continue" to proceed; nothing
 * runs automatically and no modal is force-stacked.
 *
 * Usage:
 *   suggestNextStep({ job, openIssues, navigate, runners });
 *
 * - Computes the single highest-priority next action via getJobNextAction
 * - Skips silently when there is no next action or confidence is low
 * - Uses runAction for execution (modal/direct/navigate) so consumers
 *   can wire the same payload they use for the original CTA
 */
import type { NavigateFunction } from "react-router-dom";
import { toast } from "sonner";
import type { Job, Issue } from "@/data/types";
import { getJobNextAction } from "@/data/nextActionEngine";
import { runAction, type ExecutableAction } from "@/lib/actionRunner";
import type { SuggestedAction } from "@/data/suggestedActions";

/** Map a SuggestedAction to a runner using the same modal openers a page already exposes. */
export interface NextStepRunners {
  /** Open the OrderingModal pre-filled for this job. */
  openOrdering?: (job: Job) => void;
  /** Open the RescheduleModal pre-filled for this job. */
  openReschedule?: (job: Job) => void;
  /** Open the QuickAssignModal pre-filled for this job. */
  openQuickAssign?: (job: Job) => void;
  /** Open the QuickResolveIssueModal for the top open issue on this job. */
  openResolveIssue?: (job: Job) => void;
}

interface SuggestNextStepArgs {
  job: Job;
  openIssues: Issue[];
  navigate: NavigateFunction;
  runners?: NextStepRunners;
  /** Override toast title when the previous action is fresh in user's mind. */
  successLabel?: string;
}

/** Build an ExecutableAction from a SuggestedAction + available runners. */
function toExecutable(
  action: SuggestedAction,
  job: Job,
  runners: NextStepRunners,
): ExecutableAction {
  const cta = action.cta || action.label;
  const route = action.link || `/jobs/${job.id}`;

  if (action.executionMode === "modal") {
    if (action.icon === "materials" && runners.openOrdering) {
      return { label: cta, executionMode: "modal", openModal: () => runners.openOrdering!(job) };
    }
    if (action.icon === "schedule" && runners.openReschedule) {
      return { label: cta, executionMode: "modal", openModal: () => runners.openReschedule!(job) };
    }
    if ((action.icon === "dispatch" || action.icon === "crew") && runners.openQuickAssign) {
      return { label: cta, executionMode: "modal", openModal: () => runners.openQuickAssign!(job) };
    }
    if (action.icon === "issue" && runners.openResolveIssue) {
      return { label: cta, executionMode: "modal", openModal: () => runners.openResolveIssue!(job) };
    }
  }

  return { label: cta, executionMode: "navigate", route };
}

/**
 * Surface a single next-step prompt as a toast. Safe no-op when nothing
 * is suggested. The user remains in full control — no auto-execution.
 */
export function suggestNextStep({
  job,
  openIssues,
  navigate,
  runners = {},
  successLabel,
}: SuggestNextStepArgs): void {
  // Defer slightly so it appears AFTER the success toast from the action
  // that just completed — keeps the visual order natural.
  setTimeout(() => {
    const next = getJobNextAction(job, openIssues);
    if (!next) return;

    const exec = toExecutable(next, job, runners);
    const cta = next.cta || next.label;

    toast(successLabel ?? "Nice — what's next?", {
      description: `${next.label} • ${next.description}`,
      duration: 8000,
      action: {
        label: cta,
        onClick: () => {
          runAction(exec, { navigate }).then((handled) => {
            if (!handled && next.link) navigate(next.link);
          });
        },
      },
    });
  }, 350);
}
